import dayjs, { type Dayjs } from 'dayjs';
import type { ShopDateValue, ShopInfo } from '../../../types';

export type ShopDateRange = {
  days: 7 | 30;
  label: '近7天' | '近30天';
};

export type ParsedShopDateValue = {
  shopId: string;
  startDate: string;
  endDate: string;
  shopName: string;
  rangeLabel: string;
};

export const SHOP_DATE_RANGES: ShopDateRange[] = [
  { days: 7, label: '近7天' },
  { days: 30, label: '近30天' },
];

const clean = (value: unknown): string => String(value ?? '').trim();

const inferRangeLabel = (startDate: string, endDate: string): string => {
  const days = dayjs(endDate).diff(dayjs(startDate), 'day') + 1;
  return SHOP_DATE_RANGES.find((item) => item.days === days)?.label
    ?? `${startDate} 至 ${endDate}`;
};

export const buildDateRange = (
  days: ShopDateRange['days'],
  now: Dayjs = dayjs(),
): Pick<ParsedShopDateValue, 'startDate' | 'endDate'> => {
  const end = now.subtract(1, 'day');
  return {
    startDate: end.subtract(days - 1, 'day').format('YYYY-MM-DD'),
    endDate: end.format('YYYY-MM-DD'),
  };
};

export const buildShopDateValue = (
  shop: ShopInfo,
  range: ShopDateRange,
  now?: Dayjs,
): ShopDateValue => {
  const { startDate, endDate } = buildDateRange(range.days, now);
  return [shop.shopId, startDate, endDate, shop.shopName, range.label];
};

export const parseShopDateValue = (
  value: string | string[] | undefined,
): ParsedShopDateValue | null => {
  if (!Array.isArray(value)) return null;
  const shopId = clean(value[0]);
  const startDate = clean(value[1]);
  const endDate = clean(value[2]);
  if (!shopId || !startDate || !endDate) return null;
  const shopName = clean(value[3]) || shopId;
  const rangeLabel = clean(value[4]) || inferRangeLabel(startDate, endDate);
  return { shopId, startDate, endDate, shopName, rangeLabel };
};

export const hasCompleteShopDateValue = (
  value: string | string[] | undefined,
): boolean => parseShopDateValue(value) !== null;
